import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react'
import { Form, TablePaginationConfig } from 'antd'
import { Key } from 'react'
import cn from 'classnames'
import { Table } from 'components/Table'
import { DEFAULT_PAGE } from 'components/Table/constants'
import Select from 'components/Select/Select'
import { TOption } from 'components/Select/types'
import Button from 'components/Button/Button'
import { BUTTON_TYPES } from 'components/Button/constants'
import { Typography } from 'components/Typography'
import { BTN_TXT, LABELS, PLACEHOLDERS } from 'constants/txt'
import { ReactComponent as ArrowDown16 } from 'assets/svg/ArrowDown16.svg'
import { ReactComponent as ArrowRight16 } from 'assets/svg/ArrowRight16.svg'
import { columns, FacilityTierRow } from './constants'
import { UpdateRebatePeriodFacilitiesPayload } from '../types'

type FacilityTiersTableProps = {
  rows: FacilityTierRow[]
  selectable?: boolean
  tierOptions?: TOption[]
  onSubmit?: (
    facilities: UpdateRebatePeriodFacilitiesPayload['facilities']
  ) => void
  onHasTierChanges?: (hasChanges: boolean) => void
}

export type FacilityTiersTableHandle = {
  submit: () => void
}

const collectLeaves = (rows: FacilityTierRow[]): FacilityTierRow[] =>
  rows.flatMap((row) => (row.children ? collectLeaves(row.children) : [row]))

const updateRowTier = (
  rows: FacilityTierRow[],
  uuid: string,
  tier: string
): FacilityTierRow[] =>
  rows.map((row) => {
    if (row.uuid === uuid) return { ...row, tier }
    if (!row.children) return row

    return { ...row, children: updateRowTier(row.children, uuid, tier) }
  })

const tierValues = (rows: FacilityTierRow[]) =>
  Object.fromEntries(collectLeaves(rows).map((row) => [row.uuid, row.tier]))

export const FacilityTiersTable = forwardRef<
  FacilityTiersTableHandle,
  FacilityTiersTableProps
>(
  (
    { rows, selectable = false, tierOptions = [], onSubmit, onHasTierChanges },
    ref
  ) => {
    const [form] = Form.useForm()
    const watchedTiers = Form.useWatch('tiers', form) as
      | Record<string, string | undefined>
      | undefined
    const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
    const [tableData, setTableData] = useState(rows)
    const [bulkTier, setBulkTier] = useState<string>()
    const [pendingSubmit, setPendingSubmit] = useState(false)

    useEffect(() => {
      setTableData(rows)
      setSelectedRowKeys([])
      setExpandedRowKeys([])
      setPageInfo((page) => ({ ...page, pageNumber: 1 }))
      if (selectable) form.setFieldsValue({ tiers: tierValues(rows) })
    }, [form, rows, selectable])

    const hasTierChanges = useMemo(() => {
      if (!selectable) return false

      const currentTiers = {
        ...tierValues(tableData),
        ...(watchedTiers ?? {})
      }

      return collectLeaves(rows).some(
        (row) => (currentTiers[row.uuid] ?? '') !== (row.tier ?? '')
      )
    }, [rows, selectable, tableData, watchedTiers])

    useEffect(() => {
      onHasTierChanges?.(hasTierChanges)
    }, [hasTierChanges, onHasTierChanges])

    const handleTableChange = (pagination: TablePaginationConfig) => {
      if (hasTierChanges) return

      setPageInfo({
        pageNumber: pagination.current ?? 1,
        pageSize: pagination.pageSize ?? pageInfo.pageSize
      })
    }

    const handleChangePageSize = (pageSize: number) => {
      if (hasTierChanges) return

      setPageInfo({
        pageNumber: 1,
        pageSize
      })
    }

    const onExpand = (expanded: boolean, record: FacilityTierRow) => {
      if (expanded) setExpandedRowKeys([record.uuid])
      else setExpandedRowKeys([])
    }

    const onTierChange = (uuid: string, tier: string) => {
      setTableData((current) => updateRowTier(current, uuid, tier))
    }

    const applyBulkTier = () => {
      if (!bulkTier) return

      const selected = new Set(selectedRowKeys.map(String))
      const updatedIds: string[] = []

      const applyTier = (
        row: FacilityTierRow,
        parentSelected = false
      ): FacilityTierRow => {
        const rowSelected = parentSelected || selected.has(row.uuid)

        if (row.children) {
          return {
            ...row,
            children: row.children.map((child) => applyTier(child, rowSelected))
          }
        }

        if (!rowSelected) return row

        updatedIds.push(row.uuid)
        return { ...row, tier: bulkTier }
      }

      const nextRows = tableData.map((row) => applyTier(row))
      const currentTiers = form.getFieldValue('tiers') ?? {}

      setTableData(nextRows)
      form.setFieldsValue({
        tiers: {
          ...currentTiers,
          ...Object.fromEntries(updatedIds.map((id) => [id, bulkTier]))
        }
      })
    }

    useImperativeHandle(ref, () => ({
      submit: () => {
        setExpandedRowKeys(
          tableData.filter((row) => row.children).map((row) => row.uuid)
        )
        setPageInfo((page) => ({
          pageNumber: 1,
          pageSize: Math.max(page.pageSize, tableData.length || page.pageSize)
        }))
        setPendingSubmit(true)
      }
    }))

    useEffect(() => {
      if (!pendingSubmit) return

      setPendingSubmit(false)
      form.submit()
    }, [form, pendingSubmit])

    const tierLabel = (tier?: string, isHSLevel?: boolean) =>
      tierOptions.find((option) => option.value === tier)?.label ??
      (isHSLevel ? '' : 'Not assigned')

    const tableColumns = columns.map((column) =>
      column.key === 'tier'
        ? {
            ...column,
            onCell: (record: FacilityTierRow) =>
              selectable && !record.children
                ? { className: 'tiered-hospitals-section__tier-cell' }
                : {},
            render: (_tier: string | undefined, record: FacilityTierRow) => {
              if (!selectable || record.children)
                return tierLabel(record.tier, !!record.children)

              return (
                <Select
                  options={tierOptions}
                  alwaysName
                  className="tiered-hospitals-section__tier-select"
                  propsItem={{
                    name: ['tiers', record.uuid],
                    required: true,
                    rules: [{ required: true }]
                  }}
                  propsSelect={{
                    placeholder: PLACEHOLDERS.PLEASE_SELECT,
                    showSearch: false,
                    onChange: (value) =>
                      onTierChange(record.uuid, String(value))
                  }}
                />
              )
            }
          }
        : column
    )

    const source = selectable ? tableData : rows

    const content = (
      <div className="column gap-16 relative">
        {selectable && !!selectedRowKeys.length && (
          <div className="bulk-update row gap-8 align-center">
            <Typography.Body1>{LABELS.SET_TIER}</Typography.Body1>
            <Select.DefaultNoForm
              options={tierOptions}
              className="tiered-hospitals-section__set-tier-select"
              propsSelect={{
                value: bulkTier,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: (value) => setBulkTier(String(value))
              }}
            />
            <Button
              type={BUTTON_TYPES.PRIMARY}
              upperCase
              disabled={!bulkTier}
              htmlType="button"
              onClick={applyBulkTier}
            >
              {BTN_TXT.APPLY}
            </Button>
          </div>
        )}
        <Table<FacilityTierRow>
          className={cn('tiered-hospitals-section__table', { selectable })}
          dataSource={source}
          columns={tableColumns}
          rowKey="uuid"
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: source.length,
            disabled: hasTierChanges
          }}
          rowSelection={
            selectable
              ? {
                  selectedRowKeys,
                  checkStrictly: false,
                  onChange: setSelectedRowKeys
                }
              : undefined
          }
          expandable={{
            defaultExpandAllRows: false,
            columnWidth: 40,
            expandedRowKeys,
            onExpand,
            expandIcon: ({ expanded, onExpand, record, expandable }) =>
              expandable && !!record.children?.length ? (
                expanded ? (
                  <div
                    className="expand-wrapper row gap-12 align-center"
                    onClick={(e) => onExpand(record, e)}
                  >
                    <ArrowDown16 className="arrow" />
                  </div>
                ) : (
                  <div
                    className="expand-wrapper row gap-12 align-center"
                    onClick={(e) => onExpand(record, e)}
                  >
                    <ArrowRight16 className="arrow" />
                  </div>
                )
              ) : (
                <div className="expand-wrapper" />
              )
          }}
        />
      </div>
    )

    if (!selectable) return content

    return (
      <Form
        form={form}
        component="div"
        initialValues={{ tiers: tierValues(rows) }}
        onFinish={(values: { tiers?: Record<string, string> }) => {
          const tiers = { ...tierValues(tableData), ...values.tiers }
          const facilities = collectLeaves(tableData).map((row) => ({
            uuid: row.uuid,
            tier: tiers[row.uuid]
          }))

          if (facilities.some((facility) => !facility.tier)) return

          onSubmit?.(
            facilities.map((facility) => ({
              uuid: facility.uuid,
              tier: facility.tier as string
            }))
          )
        }}
      >
        {content}
      </Form>
    )
  }
)

FacilityTiersTable.displayName = 'FacilityTiersTable'
